import { SyncOutlined } from '@ant-design/icons'
import React, { useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'
import { accents, gray } from 'sutra/tokens/colors'

import {
  FilterSelectionPopover,
  type PopoverInitialSelection,
} from 'features/charts-v2/components/FilterSelectionPopover'
import { type OperatorType as KeywordOperatorType } from 'features/charts-v2/components/KeywordOperatorPopover'
import { type SentimentMode } from 'features/charts-v2/components/SentimentFilterCard'
import {
  type FilterSelectionDraft,
  type NumericFilterFieldType,
  type NumericOperatorType,
} from 'features/charts-v2/types'
import type { FilterRestriction } from 'features/charts-v2/types/filter-restriction'
import { buildCustomerGroupValues } from 'features/charts-v2/utils/customer-group-hierarchy'
import { deduplicateFilterValues } from 'features/charts-v2/utils/filter-dedup'
import { sortFilterOptionValues } from 'features/charts-v2/utils/sort-filter-option-values'
import { buildParentThemeValues } from 'features/charts-v2/utils/theme-hierarchy'
import {
  FilterOption,
  FilterOptionSelect,
  FilterOptionValue,
  FilterTypes,
  type YogiQLFilterPreset,
} from 'features/filters/types'
import { FilterPreset } from 'types/project'

// The `+` button's hover background is purely visual; CSS `:hover`
// owns it now so moving the cursor on/off the button does not write
// `isHovered` state and therefore does not re-render FilterSelectHeader
// or its FilterSelectionPopover child. `$active` carries the open-popover
// state so the button stays blue while the popover is open.
const TriggerButton = styled.button<{ $active: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  background-color: ${({ $active }) =>
    $active ? accents.reflectionBlue[100] : '#ffffff'};
  color: #6b7280;
  font-size: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  ${({ $active }) =>
    !$active &&
    `&:hover {
      background-color: ${accents.reflectionBlue[100]};
    }`}
`

// Rendered in place of the (empty) card list when no filter is applied yet.
// Opens the same popover as `TriggerButton` — same grey/dashed vocabulary,
// but full-width and dashed so it reads as an empty-state affordance rather
// than chrome. `$variant="inline"` is the same affordance sized for a row of
// chiclets rather than a standalone card-list slot (see `emptyStateVariant`
// on the props interface below) — a small dashed pill instead of a
// full-width block.
const AddFiltersEmptyState = styled.button<{ $variant: 'block' | 'inline' }>`
  border-radius: 6px;
  border: 1px dashed #d1d5db;
  background-color: transparent;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;

  ${({ $variant }) =>
    $variant === 'inline'
      ? `
    width: auto;
    height: 24px;
    font-size: 11px;
    padding: 0 8px;
  `
      : `
    width: 100%;
    height: 40px;
    font-size: 13px;
  `}

  &:hover {
    background-color: ${gray[100]};
    border-color: ${gray[300]};
  }
`

interface FilterSelectHeaderProps {
  options: FilterOption[]
  isLoading?: boolean
  onSelect: (
    field: string,
    values?: string[],
    rangeData?: { min?: number; max?: number; valueType?: number },
  ) => void
  getCurrentSelectionsForField: (field: string) => {
    operator: string
    values: string[]
    min?: number
    max?: number
    valueType?: number
  } | null
  keywordFilters?: { [key: string]: string[] | null }
  smartSearchValue?: string | null
  onKeywordFiltersApply?: (filters: {
    [key in KeywordOperatorType]?: string[] | null
  }) => void
  onKeywordFiltersClear?: () => void
  onSmartSearchApply?: (value: string) => void
  onSmartSearchClear?: () => void
  onNumericFilterApply?: (
    field: NumericFilterFieldType,
    operator: NumericOperatorType,
    value: number,
  ) => void
  onNumericFilterClear?: (field: NumericFilterFieldType) => void
  onSentimentApply?: (draft: {
    mode: SentimentMode
    values: string[]
    numericOperator: NumericOperatorType
    numericValue: number | undefined
  }) => void
  onSentimentClear?: () => void
  // Reports the popover field whose counts should be fetched; `null` when no
  // field is expanded. PROD-3136.
  onSelectedFieldChange?: (field: string | null) => void
  onSavedFilterApply?: (preset: FilterPreset) => void
  onSavedQueryApply?: (preset: YogiQLFilterPreset) => void
  showSyncButton?: boolean
  onSyncFilters?: () => void
  // When set, the "Filter" heading is suppressed and the sync/add-filter
  // buttons portal here instead of rendering inline — see FilterControlProps'
  // `headerActionsPortalTarget` doc comment for why.
  portalTarget?: HTMLElement | null
  // When true, suppresses the "Filter" heading + its buttons row entirely —
  // no portal, nothing rendered in its place. For a caller whose own
  // surrounding chrome already carries an equivalent heading + add-filter
  // affordance (e.g. the agent create form's own "Filters" / "Suggest
  // filters" row), so this component doesn't duplicate it. The dashed
  // "+ Add filters" empty state (see `showEmptyState`) still renders and
  // remains the add-filter entry point. Default false: no existing caller
  // changes.
  hideHeaderRow?: boolean
  // Show the dashed "+ Add filters" empty-state affordance in place of the
  // (empty) card list. Owned by the caller (FilterControl), which is the one
  // place that knows about every card kind this section can render.
  showEmptyState?: boolean
  // Text of the dashed empty-state affordance. Defaults to "+ Add filters";
  // overridden by a caller whose section is scoped to one entity kind (e.g.
  // "+ Add a brand").
  emptyStateLabel?: string
  // 'block' (default): full-width 40px dashed box, sized for a standalone
  // card-list slot. 'inline': a small dashed pill sized to sit inline in a
  // row of chiclets (the benchmarking agent's Universe filter row).
  emptyStateVariant?: 'block' | 'inline'
  // Forwarded to FilterSelectionPopover's own positioning. 'right' (default)
  // anchors off the trigger's right edge with no left/right clamp — correct
  // for a sidebar with room to its right. 'center' centers the popover in
  // the viewport, for a caller with no such room (e.g. a centered inline
  // form). See FilterSelectionPopover's doc comment.
  popoverAnchorMode?: 'right' | 'center'
  // Forwarded to FilterSelectionPopover — see its `popoverPortalTarget` doc
  // comment.
  popoverPortalTarget?: HTMLElement | null
  // Forwarded to FilterSelectionPopover for the Rating/Sentiment pane
  // restrictions that can't be expressed by narrowing `options` alone
  // (valueType, include-null, sentiment mode) — see that component's use.
  // `options` itself is already narrowed by the caller (FilterControl).
  restriction?: FilterRestriction
  // Forwarded to FilterSelectionPopover — see its `fieldsOnlySidebar` doc
  // comment.
  fieldsOnlySidebar?: boolean
  // Forwarded to FilterSelectionPopover — see its `disabledFields` doc
  // comment. Also drops those fields from the "All" view's value list below.
  disabledFields?: { fields: ReadonlySet<string>; note: string }
  // Forwarded to FilterSelectionPopover — see its `onApplySelections` doc
  // comment.
  onApplySelections?: (selections: FilterSelectionDraft[]) => void
  // Lets a trigger that lives OUTSIDE this component (a "Filters" pill, a
  // search bar) open and close the popover. When set, this component renders
  // only the popover: no heading, no buttons, no "+ Add filters" box, and the
  // open state is the caller's. Default undefined: every existing caller keeps
  // the self-contained buttons above.
  externalTrigger?: ExternalPopoverTrigger
}

export type ExternalPopoverTrigger = {
  isOpen: boolean
  /** What the popover positions itself against. */
  triggerElement: HTMLElement | null
  onClose: () => void
  /** Open on a field's values or a special pane; see FilterSelectionPopover. */
  initialSelection?: PopoverInitialSelection
}

const FilterSelectHeaderComponent: React.FC<FilterSelectHeaderProps> = ({
  options,
  isLoading,
  onSelect,
  getCurrentSelectionsForField,
  keywordFilters,
  smartSearchValue,
  onKeywordFiltersApply,
  onKeywordFiltersClear,
  onSmartSearchApply,
  onSmartSearchClear,
  onNumericFilterApply,
  onNumericFilterClear,
  onSentimentApply,
  onSentimentClear,
  onSelectedFieldChange,
  onSavedFilterApply,
  onSavedQueryApply,
  showSyncButton,
  onSyncFilters,
  portalTarget,
  hideHeaderRow,
  showEmptyState,
  emptyStateLabel = '+ Add filters',
  emptyStateVariant = 'block',
  popoverAnchorMode,
  popoverPortalTarget,
  restriction,
  fieldsOnlySidebar,
  disabledFields,
  onApplySelections,
  externalTrigger,
}) => {
  const [internalShowPopover, setShowPopover] = useState(false)
  const showPopover = externalTrigger?.isOpen ?? internalShowPopover
  const [tooltipOpen, setTooltipOpen] = useState(false)
  // Set from whichever button was actually clicked (the header `+` or the
  // "+ Add filters" empty state) — not a static ref bound to one physical
  // button. Both can trigger the same popover; anchoring off a fixed ref
  // that isn't the button the user clicked is exactly the mispositioning bug
  // this fixes (previously: always the header `+`, even when the empty
  // state's dashed box is what got clicked).
  const [internalTriggerElement, setTriggerElement] =
    useState<HTMLElement | null>(null)
  const triggerElement = externalTrigger
    ? externalTrigger.triggerElement
    : internalTriggerElement

  // Pre-compute flattened values for "All" view
  const flattenedChildValues = useMemo(() => {
    const flattened: Array<{
      parentField: string
      parentAlias: string
      value: FilterOptionValue
      displayText: string
      kind?: 'parent'
    }> = []

    options
      .filter(
        (f) =>
          f.field !== 'star' &&
          f.field !== 'create_time' &&
          !disabledFields?.fields.has(f.field),
      )
      .forEach((option) => {
        if (option.filter_type === FilterTypes.SELECT) {
          const selectFilter = option as FilterOptionSelect
          const parentAlias = option.alias || option.field
          // Theme/proj_uuid filters carry multiple rows per display name
          // (one per cluster_uuid). The "Theme" detail view runs them
          // through `deduplicateFilterValues` so a single row represents
          // the merged set; do the same here so the "All" view shows one
          // row per unique theme name with the summed post count.
          const childValues =
            option.field === 'theme' || option.field === 'proj_uuid'
              ? deduplicateFilterValues(selectFilter.values || [])
              : selectFilter.values || []
          // A theme parent ("Packaging") gets its own row beside its
          // "Packaging: <child>" rows; the popover's A-Z sort lists it first.
          if (option.field === 'theme') {
            buildParentThemeValues(childValues).forEach((value) => {
              flattened.push({
                parentField: option.field,
                parentAlias,
                value,
                displayText: `${parentAlias} > ${value.value}`,
                kind: 'parent',
              })
            })
          }
          // "My Brands" / "Competitor Brands" / "My Products" /
          // "Competitor Products" get their own rows ahead of the values
          // they group, like a theme parent.
          buildCustomerGroupValues(option).forEach((value) => {
            flattened.push({
              parentField: option.field,
              parentAlias,
              value,
              displayText: `${parentAlias} > ${value.value}`,
              kind: 'parent',
            })
          })
          // Sort child values per parent field. Cross-field order
          // (the order of parent fields themselves) stays as-is.
          const sortedChildValues = sortFilterOptionValues(childValues)
          sortedChildValues.forEach((value) => {
            flattened.push({
              parentField: option.field,
              parentAlias,
              value,
              displayText: `${parentAlias} > ${value.value}`,
            })
          })
        }
      })

    return flattened
  }, [options, disabledFields])

  const handleSelect = useCallback(
    (
      field: string,
      values?: string[],
      rangeData?: { min?: number; max?: number; valueType?: number },
    ): void => {
      onSelect(field, values, rangeData)
      // Don't close popover automatically - let user close it manually
    },
    [onSelect],
  )

  const externalOnClose = externalTrigger?.onClose
  const handleClosePopover = useCallback((): void => {
    setShowPopover(false)
    setTooltipOpen(false)
    externalOnClose?.()
  }, [externalOnClose])

  const syncTooltip = (
    <div>
      <div>Copy these filters to other panels</div>
      <div style={{ marginTop: '4px', color: 'gray' }}>
        This will overwrite the existing filters on other panels
      </div>
    </div>
  )

  const buttons = (
    <>
      {showSyncButton && (
        <Tooltip content={syncTooltip}>
          <TriggerButton
            type="button"
            $active={false}
            onClick={() => onSyncFilters?.()}
            aria-label="Copy these filters to other panels"
          >
            <SyncOutlined style={{ fontSize: '16px' }} />
          </TriggerButton>
        </Tooltip>
      )}
      <Tooltip
        open={tooltipOpen}
        content="Filter to include or exclude specific data."
        side="right"
      >
        <TriggerButton
          type="button"
          $active={showPopover}
          onClick={(e) => {
            setTooltipOpen(false)
            setTriggerElement(e.currentTarget)
            setShowPopover(!showPopover)
          }}
          onMouseEnter={() => {
            if (!showPopover) setTooltipOpen(true)
          }}
          onMouseLeave={() => {
            setTooltipOpen(false)
          }}
        >
          +
        </TriggerButton>
      </Tooltip>
    </>
  )

  const popover = showPopover && (
    <FilterSelectionPopover
      filterOptions={options}
      isLoading={isLoading}
      precomputedFlattenedValues={flattenedChildValues}
      onFilterSelect={handleSelect}
      onClose={handleClosePopover}
      getFilterSelection={getCurrentSelectionsForField}
      triggerElement={triggerElement}
      anchorMode={popoverAnchorMode}
      popoverPortalTarget={popoverPortalTarget}
      keywordFilters={keywordFilters}
      smartSearchValue={smartSearchValue}
      onKeywordFiltersApply={onKeywordFiltersApply}
      onKeywordFiltersClear={onKeywordFiltersClear}
      onSmartSearchApply={onSmartSearchApply}
      onSmartSearchClear={onSmartSearchClear}
      onNumericFilterApply={onNumericFilterApply}
      onNumericFilterClear={onNumericFilterClear}
      onSentimentApply={onSentimentApply}
      onSentimentClear={onSentimentClear}
      onSelectedFieldChange={onSelectedFieldChange}
      onSavedFilterApply={onSavedFilterApply}
      onSavedQueryApply={onSavedQueryApply}
      restriction={restriction}
      fieldsOnlySidebar={fieldsOnlySidebar}
      disabledFields={disabledFields}
      onApplySelections={onApplySelections}
      initialSelection={externalTrigger?.initialSelection}
    />
  )

  // Opens with `setShowPopover(true)`, not a toggle — this button unmounts
  // the moment a filter is applied, so toggle semantics have no meaning here.
  const emptyState = showEmptyState && (
    <AddFiltersEmptyState
      type="button"
      $variant={emptyStateVariant}
      onClick={(e) => {
        setTooltipOpen(false)
        setTriggerElement(e.currentTarget)
        setShowPopover(true)
      }}
    >
      {emptyStateLabel}
    </AddFiltersEmptyState>
  )

  if (externalTrigger) return <>{popover}</>

  if (hideHeaderRow) {
    return (
      <>
        {emptyState}
        {popover}
      </>
    )
  }

  // The caller (ControlsSidebar, when 'filter' is its only section) already
  // renders a "Filter" heading in its own header row — rendering ours too
  // would duplicate it. Portal just the buttons there instead; the popover
  // still renders here (and self-portals to `document.body`), positioned off
  // whichever button was actually clicked regardless of where it sits.
  if (portalTarget) {
    return (
      <>
        {createPortal(buttons, portalTarget)}
        {emptyState}
        {popover}
      </>
    )
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingLeft: '8px',
          position: 'relative',
        }}
      >
        <h2 style={{ margin: 0, fontSize: '14px', fontWeight: '600' }}>
          Filter
        </h2>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {buttons}
        </div>

        {popover}
      </div>
      {emptyState}
    </>
  )
}

// React.memo with default shallow-equal — `tooltipOpen` and
// `showPopover` are component-internal so their toggles don't propagate
// upward; the only props that change FilterSelectHeader's render are
// the ones supplied by FilterControl. Callers wrap their handler props
// in `useCallback` to keep the memo barrier honest.
export const FilterSelectHeader = React.memo(FilterSelectHeaderComponent)
